import { Container, Rectangle, Sprite, Texture, type GenerateTextureOptions } from 'pixi.js';
import type { CameraView } from '@/modules/game-domain/render-snapshot';
import type { BodyRect } from './actor-visibility';
import { compareGroundDepth } from './depth-order';
import type { ScreenBounds } from './terrain-presentation';

export type FarProp = {
  wx: number; wy: number; poolKey: string; textureKey: string; texture: Texture;
  isoX: number; isoY: number; anchorX: number; anchorY: number; scale: number;
  left: number; right: number; top: number; bottom: number; bodyRect: BodyRect;
};
export type PropDepthCut = { label: string; zIndex: number; bounds: ScreenBounds };
type Tile = { key: string; x: number; y: number; size: number; resolution: number;
  props: readonly FarProp[]; indices: Map<string, number> };
type Plan = { tile: Tile; cuts: number[]; excluded: number[]; excludedKey: string };
type Group = { key: string; tile: Tile; first: number; end: number; excluded: ReadonlySet<string> };
type Entry = { sprite: Sprite; texture: Texture; bytes: number };
const CACHE_CAPACITY = 256;
const TILE_CAPACITY = 96;
const intersects = (a: ScreenBounds, b: ScreenBounds) => a.minX <= b.maxX && a.maxX >= b.minX
  && a.minY <= b.maxY && a.maxY >= b.minY;
const depth = (p: FarProp) => ({ zIndex: p.isoY, label: p.poolKey });

/** First prop after a total (depth, identity) key; actor motion inside a gap reuses the same interval. */
export function depthCutIndex(props: readonly FarProp[], cut: Pick<PropDepthCut, 'zIndex' | 'label'>): number {
  let low = 0, high = props.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (compareGroundDepth(depth(props[mid]!), cut) <= 0) low = mid + 1;
    else high = mid;
  }
  return low;
}

/** Exact static art, clipped to disjoint output rectangles and split around every external depth key.
 * Only generated low-resolution textures are owned here; asset textures are shared with the game/editor.
 */
export class FarPropCache {
  private tiles = new Map<string, Tile>();
  private entries = new Map<string, Entry>();
  private owned = new WeakSet<Container>();
  private builds = 0;
  private fallbackCount = 0;
  constructor(private readonly layer: Container,
    private readonly generate: (options: GenerateTextureOptions) => Texture,
    private readonly query: (bounds: ScreenBounds) => readonly FarProp[],
    private readonly capacity = CACHE_CAPACITY,
    private readonly tileCapacity = TILE_CAPACITY) {}

  owns(child: Container): boolean { return this.owned.has(child); }
  get stats() {
    let bytes = 0, visible = 0;
    for (const entry of this.entries.values()) { bytes += entry.bytes; if (entry.sprite.visible) visible++; }
    return { tiles: this.tiles.size, textures: this.entries.size, visible, bytes, builds: this.builds, fallbackProps: this.fallbackCount };
  }
  hide(): void { for (const entry of this.entries.values()) { entry.sprite.visible = false; entry.sprite.removeFromParent(); } }
  clear(): void {
    for (const entry of this.entries.values()) this.release(entry);
    this.entries.clear(); this.tiles.clear(); this.fallbackCount = 0;
  }
  private release(entry: Entry): void { entry.sprite.destroy({ texture: true }); entry.texture.destroy(true); }

  update(camera: CameraView, width: number, height: number, stride: number,
    external: readonly PropDepthCut[], heroExcluded: ReadonlySet<string>): readonly FarProp[] {
    const size = 256 * stride, resolution = .5 / stride;
    const halfW = width / (2 * (camera.zoom ?? 1)) + 96;
    const halfH = height / (2 * (camera.zoom ?? 1)) + 96;
    const active: Tile[] = [];
    for (let x = Math.floor((camera.finalX - halfW) / size); x <= Math.floor((camera.finalX + halfW) / size); x++) {
      for (let y = Math.floor((camera.finalY - halfH) / size); y <= Math.floor((camera.finalY + halfH) / size); y++) {
        const key = `${stride}:${x},${y}`;
        let tile = this.tiles.get(key);
        if (tile) { this.tiles.delete(key); this.tiles.set(key, tile); }
        else {
          const gutter = 2 / resolution;
          const props = [...this.query({ minX: x * size - gutter, maxX: (x + 1) * size + gutter,
            minY: y * size - gutter, maxY: (y + 1) * size + gutter })].sort((a, b) => compareGroundDepth(depth(a), depth(b)));
          tile = { key, x: x * size, y: y * size, size, resolution, props,
            indices: new Map(props.map((p, i) => [p.poolKey, i])) };
          this.tiles.set(key, tile);
        }
        active.push(tile);
      }
    }
    const activeTiles = new Set(active.map(tile => tile.key));
    for (const key of this.tiles.keys()) {
      if (this.tiles.size <= Math.max(this.tileCapacity, active.length)) break;
      if (!activeTiles.has(key)) this.tiles.delete(key);
    }
    const cuts = new Map<Tile, number[]>();
    const excluded = new Set(heroExcluded);
    for (const tile of active) {
      const bounds = { minX: tile.x, maxX: tile.x + tile.size, minY: tile.y, maxY: tile.y + tile.size };
      const indices = new Set([0, tile.props.length]);
      for (const actor of external) if (intersects(bounds, actor.bounds)) indices.add(depthCutIndex(tile.props, actor));
      const sorted = [...indices].sort((a, b) => a - b);
      cuts.set(tile, sorted);
    }
    const makePlans = (): Plan[] => active.map(tile => {
      const omitted = [...excluded].flatMap(key => { const index = tile.indices.get(key); return index === undefined ? [] : [index]; }).sort((a,b) => a-b);
      return { tile, cuts: [...new Set([...cuts.get(tile)!, ...omitted.map(index => index + 1)])].sort((a,b) => a-b), excluded: omitted, excludedKey: omitted.join(',') };
    });
    const groupsFor = (plans: readonly Plan[]): Group[] => plans.flatMap(plan => {
      const output: Group[] = [];
      for (let i = 1; i < plan.cuts.length; i++) {
        const first = plan.cuts[i - 1]!, end = plan.cuts[i]!;
        if (end - first <= plan.excluded.filter(index => index >= first && index < end).length) continue;
        output.push({ key: `${plan.tile.key}:${first}-${end}:${plan.excludedKey}`, tile: plan.tile, first, end, excluded });
      }
      return output;
    });
    let plans = makePlans(), groups = groupsFor(plans);
    for (;;) {
      const counts = new Map<Tile, number>();
      for (const group of groups) counts.set(group.tile, (counts.get(group.tile) ?? 0) + 1);
      const sorted = [...counts].sort((a,b) => b[1] - a[1]);
      if (groups.length <= this.capacity) break;
      const busiest = sorted[0]![0];
      for (const prop of busiest.props) excluded.add(prop.poolKey);
      plans = makePlans(); groups = groupsFor(plans);
    }
    const requested = new Set(groups.map(group => group.key));
    let missing = 0;
    for (const key of requested) if (!this.entries.has(key)) missing++;
    // Evict before allocating, so a sweeping pan never doubles the GPU budget temporarily.
    for (const [key, entry] of this.entries) {
      if (this.entries.size + missing <= this.capacity) break;
      if (!requested.has(key)) { this.entries.delete(key); this.release(entry); }
    }
    for (const [key, entry] of this.entries) if (!requested.has(key)) {
      entry.sprite.visible = false; entry.sprite.removeFromParent();
    }
    for (const group of groups) {
      let entry = this.entries.get(group.key);
      if (entry) { this.entries.delete(group.key); this.entries.set(group.key, entry); }
      else { entry = this.bake(group); this.entries.set(group.key, entry); }
      entry.sprite.visible = true;
      if (!entry.sprite.parent) this.layer.addChild(entry.sprite);
    }
    const individual = new Map<string, FarProp>();
    for (const tile of active) for (const key of excluded) {
      const index = tile.indices.get(key);
      if (index !== undefined) individual.set(key, tile.props[index]!);
    }
    this.fallbackCount = individual.size;
    return [...individual.values()];
  }

  private bake(group: Group): Entry {
    const { tile } = group, scene = new Container(); scene.sortableChildren = true;
    let last: FarProp | undefined;
    try {
      for (let i = group.first; i < group.end; i++) {
        const p = tile.props[i]!;
        if (group.excluded.has(p.poolKey)) continue;
        const sprite = new Sprite(p.texture);
        sprite.position.set(p.isoX, p.isoY); sprite.anchor.set(p.anchorX, p.anchorY); sprite.scale.set(p.scale);
        sprite.zIndex = p.isoY; sprite.label = p.poolKey; scene.addChild(sprite); last = p;
      }
      const gutter = 2 / tile.resolution;
      const texture = this.generate({ target: scene, frame: new Rectangle(tile.x - gutter, tile.y - gutter,
        tile.size + gutter * 2, tile.size + gutter * 2), resolution: tile.resolution });
      texture.source.scaleMode = 'linear';
      const view = new Texture({ source: texture.source, frame: new Rectangle(gutter, gutter, tile.size, tile.size) });
      const sprite = new Sprite(view);
      sprite.label = last!.poolKey; sprite.zIndex = last!.isoY;
      sprite.position.set(tile.x, tile.y); sprite.eventMode = 'none'; this.owned.add(sprite); this.builds++;
      return { sprite, texture, bytes: texture.source.pixelWidth * texture.source.pixelHeight * 4 };
    } finally { scene.destroy({ children: true }); }
  }
}
